/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { useQuery } from '@apollo/client';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { useRecoilCleanup } from 'hooks/useRecoilCleanup';

import * as K from 'common/constants';
import { get } from 'lodash';
import { LogEntryEdge } from 'common/graphqlTypes';

import { LIVE_TAIL_QUERY } from 'components/search/LiveTailPage/queries';
import LiveTailLogResults from 'components/search/LiveTailPage/LiveTailLogResults';
import LiveTailHeader from 'components/search/LiveTailPage/LiveTailHeader';
import LiveTailToolbar from 'components/search/LiveTailPage/LiveTailToolbar';

import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import Button from 'components/common/Button/Button';
import LoglineSelectionPopover from 'components/search/SearchLogsPage/LoglineSelectionPopover';
import SeeMoreDialog from 'components/common/Dialog/SeeMoreDialog';
import { draftSearchParamState, searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import { viewState } from 'components/common/Managers/ViewStateManager.state';
import { isLiveTailActiveState, useGetFullLogData } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import history from 'service/customHistory';
import { updateUrlParamsString } from 'common/util/location';
import { buildParamsWithDeconstructedFilter } from 'common/util/filter/filter';
import { UrlParams } from 'common/types';
import { isFullScreenModeState } from 'components/other/MainNavBar/MainNavBar.state';

import './LiveTailPage.scss';

const MAX_LOG_NUM = 1000;
const LIVE_TAIL_SPEED_MS = 50;
const POLL_INTERVAL = 5000;
const ALLOWED_INACTIVITY_SEC = 600;
const SHOW_ALERT_TIME = 30;

/**
 * Live Tail page component.
 *
 * How Live Tail works:
 * In the initial load, it tries to query live tail logs with a filter from the URL.
 * There are three possible results from the initial query.
 *   1. a response with an error or a loading status. We return a null.
 *
 *   2. a response with logs. It adds the new logs to a queue and periodically appends
 *      a new log from the queue to the running live tails. When the queue becomes empty, it triggers
 *      startPolling() and periodically makes a query until the response comes back with logs.
 *
 *   3. a response but no logs. It triggers startPolling(), which
 *      periodically makes a GraphQL query until the response comes back with logs.
 *      Then it repeats what 2) does.
 *
 * When Live Tail is paused, it immediately stops polling. All the outstanding logs in the queue
 * remains until Live Tail is resumed.
 * When Live Tail is resumed, it first deals with the outstanding logs until it's empty then startPolling().
 */

function LiveTailPage(): JSX.Element {
  const setIsFullScreenMode = useSetRecoilState(isFullScreenModeState);
  const [isActive, setIsActive] = useRecoilState(isLiveTailActiveState);
  const [liveTailLogs, setLiveTailLogs] = useState<LogEntryEdge[]>(K.EMPTY_ARRAY);
  const [outstandingLogs, setOutstandingLogs] = useState<LogEntryEdge[]>(K.EMPTY_ARRAY);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const setSearchParams = useSetSearchParams();
  const searchParams = useRecoilValue(searchParamRecordState);
  const view = useRecoilValue(viewState);
  const filter = searchParams[K.URL_PARAM.FILTER] || '';
  const prevFilter = usePrevious(filter);
  const { loading, error, data, refetch, startPolling, stopPolling } = useQuery(LIVE_TAIL_QUERY, {
    variables: { filter },
    fetchPolicy: 'no-cache',
  });
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [remainingSeconds, setRemainingSeconds] = useState(ALLOWED_INACTIVITY_SEC);
  useRecoilCleanup(isFullScreenModeState);

  // generate and handle 'getLogEntry' requests for full data for a log line
  useGetFullLogData();

  const exitLiveTail = (route = K.ROUTE.EVENTS): void => {
    history.push({
      pathname: route,
      search: updateUrlParamsString(buildParamsWithDeconstructedFilter(searchParams, view), {
        [K.URL_PARAM.MODE]: '',
      } as UrlParams),
    });
  };

  // set inactivity timer when live tail starts or resumes
  const prevIsActive = usePrevious(isActive);
  useConditionalEffect(() => {
    setRemainingSeconds(ALLOWED_INACTIVITY_SEC);
  }, !prevIsActive && isActive);

  useConditionalEffect(() => {
    // perform a refetch when the filter changes, and kick off live tail.
    refetch({ filter });
    setIsActive(true);
  }, filter !== prevFilter && prevFilter !== undefined);

  const performRefetch = useRecoilCallback(({ snapshot }) => (): void => {
    const draftParams = snapshot.getLoadable(draftSearchParamState).contents as UrlParams;
    setSearchParams({ filter: draftParams[K.URL_PARAM.FILTER] } as UrlParams);
  });

  // hide the page alert and kick off live tail.
  const hideAlertAndResetTimer = (): void => {
    setPageAlertConfig({ message: null });
    setIsActive(true);
  };

  // set live tail to full screen always.
  // useLayoutEffect is called after DOM mutation but before painted.
  // It's a reasonable place to set full screen.
  useLayoutEffect(() => {
    setIsFullScreenMode(true);
    document.title = 'Live Tail: ' + document.title;
  }, []);

  // this effect handles the timer that auto-pause live tail
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (remainingSeconds === 0) {
        setIsActive(false);
      } else if (isActive) {
        if (remainingSeconds === SHOW_ALERT_TIME) {
          setPageAlertConfig({
            message: (
              <>
                {`Due to inactivity, live tail will automatically pause in ${SHOW_ALERT_TIME} seconds. `}
                <Button onClick={(): void => hideAlertAndResetTimer()}>Don&apos;t pause</Button>
              </>
            ),
            autoHide: true,
            animateClass: 'LiveTailPage--PageAlertDropin',
            duration: 10,
          });
        }
        setRemainingSeconds((seconds) => seconds - 1);
      }
    }, 1000);
    return (): void => {
      clearTimeout(timeout);
    };
  }, [remainingSeconds]);

  // this effect adds new logs to outstandingLogs.
  useEffect(() => {
    if (data) {
      const logEntryEdges = get(data, 'liveTail.getLogEntries', K.EMPTY_ARRAY) as LogEntryEdge[];
      const existingTokens = new Set(liveTailLogs.map((log) => log.continuationToken));
      const pendingTokens = new Set(outstandingLogs.map((log) => log.continuationToken));
      const logsToAppend = logEntryEdges.filter(
        (edge) => !existingTokens.has(edge.continuationToken) && !pendingTokens.has(edge.continuationToken),
      );
      setOutstandingLogs(logsToAppend);
      if (logsToAppend.length === 0) {
        startPolling(POLL_INTERVAL);
      } else {
        stopPolling();
      }
    }
  }, [data]);

  useEffect(() => {
    if (isActive) {
      if (outstandingLogs.length === 0) {
        startPolling(POLL_INTERVAL);
      } else {
        const setTimeoutPromise = setTimeout(() => {
          const oneLog = outstandingLogs.slice(0, 1);
          // typescript warns oneLog can be undefined
          if (oneLog) {
            const newLogs = liveTailLogs.slice(liveTailLogs.length > MAX_LOG_NUM ? 1 : 0);
            newLogs.push(oneLog[0]);
            setLiveTailLogs(newLogs);
            setOutstandingLogs(outstandingLogs.slice(1));
          }
        }, LIVE_TAIL_SPEED_MS);
        timerRef.current = setTimeoutPromise;
      }
    }
    return (): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      stopPolling();
    };
  }, [outstandingLogs, isActive]);

  // this effect handles ESC key to exit live tail.
  useEffect(() => {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    function handleKeyDown(ev: any): void {
      if (ev.key === 'Escape') {
        exitLiveTail();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return (): void => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (error) {
    return <></>;
  }

  if (loading && liveTailLogs.length === 0) {
    return <LoadingPlaceholder />;
  }

  return (
    <>
      <div className="LiveTailPage">
        <LiveTailHeader exitLiveTail={exitLiveTail} />
        <LiveTailToolbar performRefetch={performRefetch} />
        <LiveTailLogResults liveTailLogs={liveTailLogs} />
      </div>
      <LoglineSelectionPopover />
      <SeeMoreDialog />
    </>
  );
}

export default LiveTailPage;
